<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :columnDefs="columnDefs"
            :rowData="rowData"
            :getRowStyle="getRowStyle"
            :isAutoWidth="true"
            :isSearch="!isMobile"
            :pinnedBottomRowData="pinnedBottomRowData"
            :statusBar="statusBar"
            gridTheme="ag-theme-alpine-report"
        >
            <div class="d-flex align-items-start">
                <datepicker
                    :value="date"
                    :display-format="userUnicodeDateFormat"
                    :responsiveness="isMobile"
                    @input="handleDateChange"
                    variant="primary"
                    class="h-100 mr-1"
                />
                <responsive-dropdown  :icon="['fas', 'filter']" :items="reportOnOptions" variant="primary">
                    {{ reportLabels[reportOn] }}
                </responsive-dropdown>
                <export-to-excel-button class="h-100" :href="exportExcelHref"></export-to-excel-button>
                <exportToPDFButton
                    class="h-100"
                    :href="exportPdfHref"
                    :pdfCacheKey="pdfCacheKey"
                    :pageOrientation="pageOrientation.toLowerCase()"
                />
                <responsive-dropdown
                    :icon="['fas', 'file']"
                    :items="pageOrientationOptions"
                    variant="primary"
                >
                    {{ pageOrientation }}
                </responsive-dropdown>
                <report-settings-modal :modalId="reportSettingsModalId" :initialValues="reportSettings" />
            </div>
        </grid>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import API from '../../api';
    import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
    import Page from '../generic/page/page.vue';
    import Grid from '../generic/grid/grid.vue';
    import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
    import FormSelect from '../generic/form-select/form-select.vue';
    import Datepicker from '../generic/datepicker/datepicker.vue';
    import ReportSummaryBar from './report-summary-bar.vue';
    import ReportSettingsModal, { DATE_PICKER_INPUT, SELECT_INPUT } from './report-settings/report-settings-modal.vue';
    import { REPORT_ADVANCE_DEPOSITS_ENDPOINT } from '../../endpoints';
    import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
    import { toAmountDisplay } from '../../utils/numbers';
import { defineAsyncComponent } from 'vue';

    const REPORT_ON_INVOICE_START = 'INVOICE_START';
    const REPORT_ON_INVOICE_END = 'INVOICE_END';
    const PAGE_ORIENTATION_PORTRAIT = 'Portrait';
    const PAGE_ORIENTATION_LANDSCAPE = 'Landscape';
    const reportLabels = {
        INVOICE_START: 'Invoice Start Date',
        INVOICE_END: 'Invoice End Date',
    };

    const exportToPDFButton = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-export-to-pdf-button" */
            '../generic/export-to-pdf-button/export-to-pdf-button.vue'
        ))

    export default {
        name: 'ReportAdvanceDeposits',
        components: {
            Page,
            Grid,
            ResponsiveDropdown,
            ExportToExcelButton,
            exportToPDFButton,
            FormSelect,
            Datepicker,
            reportSummaryBar: ReportSummaryBar,
            ReportSettingsModal,
        },
        mixins: [removeScrollMixin],
        data() {
            return {
                title: 'Advance Deposits',
                date: format(new Date(), 'yyyy-MM-dd'),
                busy: null,
                columnApi: null,
                gridApi: null,
                reportOn: REPORT_ON_INVOICE_START,
                pageOrientation: PAGE_ORIENTATION_LANDSCAPE,
                rowData: [],
                pdfCacheKey: null,
                total: {},
                getRowStyle: null,
                reportLabels,
                windowWidth: 0,
                windowHeight: 0,
                tabs: [
                    {
                        title: 'Advance Deposits',
                        route: '/app/reports/advance-deposits/',
                        active: true,
                    },
                ],
                statusBar: null,
                reportSettingsModalId: 'report-settings-modal-id',
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol', 'isImpersonated']),
            columnDefs() {
                return [
                    {
                        field: 'number',
                        headerName: '#',
                        minWidth: 60,
                    },
                    {
                        field: 'start_date',
                        headerName: 'Inv Start',
                        type: 'dateColumn',
                        minWidth: 100,
                    },
                    {
                        field: 'end_date',
                        headerName: 'Inv End',
                        type: 'dateColumn',
                        minWidth: 100,
                    },
                    {
                        field: 'created',
                        headerName: 'Booking Date',
                        type: 'dateColumn',
                        minWidth: 105,
                    },
                    {
                        field: 'id',
                        headerName: 'Inv #',
                        minWidth: 100,
                    },
                    {
                        field: 'rental_property',
                        headerName: 'Property',
                        minWidth: 200,
                        autoWidthExclude: true,
                    },
                    {
                        field: 'first_name',
                        headerName: 'First Name',
                        minWidth: 130,
                    },
                    {
                        field: 'last_name',
                        headerName: 'Last Name',
                        minWidth: 130,
                    },
                    {
                        field: 'charges',
                        headerName: 'Charges',
                        minWidth: 100,
                        type: 'amountColumn',
                    },
                    {
                        field: 'paid',
                        headerName: 'Paid',
                        minWidth: 100,
                        type: 'amountColumn',
                    },
                    {
                        field: 'res_deposit',
                        headerName: 'Res Dep',
                        minWidth: 100,
                        type: 'amountColumn',
                    },
                    {
                        field: 'other_deposit_advance',
                        headerName: 'Other Dep',
                        minWidth: 100,
                        type: 'amountColumn',
                    },
                ];
            },
            pinnedBottomRowData() {
                return [
                    {
                        charges: this.total?.charges || 0,
                        paid: this.total?.paid || 0,
                        res_deposit: this.total?.res_deposit || 0,
                        other_deposit_advance: this.total?.other_deposit_advance || 0,
                    },
                ];
            },
            reportOnOptions() {
                return [
                    {
                        label: this.reportLabels[REPORT_ON_INVOICE_START],
                        click: () => {
                            this.reportOn = REPORT_ON_INVOICE_START;
                            this.fetchData();
                        },
                    },
                    {
                        label: this.reportLabels[REPORT_ON_INVOICE_END],
                        click: () => {
                            this.reportOn = REPORT_ON_INVOICE_END;
                            this.fetchData();
                        },
                    },
                ];
            },
            exportExcelHref() {
                return (
                    `/app/reports/advance-deposits/row-data/excel/?report_on=${this.reportOn}` +
                    `&recorded_by=${this.date}`
                );
            },
            exportPdfHref() {
                return `/app/reports/advance-deposits/row-data/pdf/`;
            },
            pageOrientationOptions() {
                return [
                    {
                        label: PAGE_ORIENTATION_LANDSCAPE,
                        click: () => {
                            this.pageOrientation = PAGE_ORIENTATION_LANDSCAPE;
                        },
                    },
                    {
                        label: PAGE_ORIENTATION_PORTRAIT,
                        click: () => {
                            this.pageOrientation = PAGE_ORIENTATION_PORTRAIT;
                        },
                    },
                ];
            },
            isMobile() {
                const md = 768;
                return this.windowWidth < md || this.windowHeight < md;
            },
            reportSettings() {
                return {
                    name: 'Advance Deposits',
                    report_sysname: 'ADVANCE_DEPOSITS',
                    settings: [
                        {
                            type: DATE_PICKER_INPUT,
                            value: format(new Date(), 'yyyy-MM-dd'),
                            label: 'Date',
                            name: 'recorded_by',
                        },
                        {
                            type: SELECT_INPUT,
                            value: REPORT_ON_INVOICE_START,
                            label: 'Report On',
                            name: 'report_on',
                            options: [
                                {
                                    text: this.reportLabels[REPORT_ON_INVOICE_START],
                                    value: REPORT_ON_INVOICE_START,
                                },
                                {
                                    text: this.reportLabels[REPORT_ON_INVOICE_END],
                                    value: REPORT_ON_INVOICE_END,
                                },
                            ],
                        },
                    ],
                };
            },
        },
        mounted() {
            this.fetchData();
            const grid = this.$refs.grid;
            this.columnApi = grid.gridOptions.columnApi;
            this.gridApi = grid.gridOptions.api;
            this.onResize();
            window.addEventListener('resize', this.onResize);
        },
        beforeUnmount() {
            window.removeEventListener('resize', this.onResize);
        },
        beforeMount() {
            this.getRowStyle = (params) => {
                if (params.node.rowPinned) {
                    return { 'font-weight': 'bold' };
                }
            };
            this.statusBar = {
                statusPanels: [
                    {
                        key: 'reportSummaryBar',
                        statusPanel: 'reportSummaryBar',
                        align: 'left',
                    },
                ],
            };
        },
        methods: {
            async fetchData() {
                try {
                    this.busy = true;
                    const response = await API.get(REPORT_ADVANCE_DEPOSITS_ENDPOINT(), {
                        params: {
                            report_on: this.reportOn,
                            recorded_by: this.date,
                        },
                    });
                    const data = response.data.items;
                    this.rowData = data.guest_infos;
                    this.pdfCacheKey = response.data.pdf_cache_key;
                    this.total = {
                        charges: data.total_charges,
                        paid: data.total_paid,
                        res_deposit: data.total_res_deposit,
                        other_deposit_advance: data.total_other_deposit_advance,
                    };
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch data' });
                } finally {
                    this.busy = false;
                }
            },
            handleDateChange(value) {
                this.date = value;
                this.fetchData();
            },
            onResize() {
                this.windowWidth = window.innerWidth;
                this.windowHeight = window.innerHeight;
                const statusBarComponent = this.gridApi.getStatusPanel('reportSummaryBar');
                let componentInstance = statusBarComponent;
                if (statusBarComponent.getFrameworkComponentInstance) {
                    componentInstance = statusBarComponent.getFrameworkComponentInstance();
                }
                componentInstance.setVisible(this.isMobile);
                componentInstance.setTitle(this.title);
            },
        },
    };
</script>
